import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import adminApi from "../../api/adminApi";
import type { DataCatalogueEntityType } from "../../api/types/fileTypes";

interface Props {
  catalogueId: string;
  fileName: string;
  dataCatalogEntityType: DataCatalogueEntityType;
  objectType: string;
  objectId: string;
  open: boolean;
  onClose: () => void;
  onDeleted: (catalogueId: string) => void;
}

const detachEntityFiles = withErrorHandling(adminApi.detachEntityFiles);

const DeleteDocumentDialog = ({
  catalogueId,
  fileName,
  dataCatalogEntityType,
  objectType,
  objectId,
  open,
  onClose,
  onDeleted,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    const [resp, error] = await detachEntityFiles(dataCatalogEntityType, objectId, objectType, [catalogueId]);
    setDeleting(false);

    if (error || !resp.result) {
      sendNotificationError(`Failed to delete ${fileName}`);
    } else {
      sendNotification(`${fileName} deleted successfully`);
      onDeleted(catalogueId);
    }
  };

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Delete File</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={0.5} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Delete file?</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              You&apos;re about to delete <strong>{fileName}</strong>
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              This action is permanent and can&apos;t be undone.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={deleting} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteDocumentDialog;
